import { motion } from "framer-motion";
import {
  Anchor,
  BookOpen,
  ExternalLink,
  Film,
  Heart,
  Image as ImageIcon,
  MapPin,
  MessageCircleHeart,
  Music,
  Quote,
  Trash2,
  UserRound,
  Sparkles,
  Blocks,
} from "lucide-react";
import { MOSAIC_TYPES, rotationForId, offsetForId } from "@/lib/mosaicData";
import { MOSAIC } from "@/constants/testIds";

const ICONS = {
  quote: Quote,
  image: ImageIcon,
  music: Music,
  film: Film,
  book: BookOpen,
  mapPin: MapPin,
  heart: Heart,
  messageCircleHeart: MessageCircleHeart,
  anchor: Anchor,
  userRound: UserRound,
  sparkles: Sparkles,
  blocks: Blocks,
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const REFERENCE_TYPES = new Set(["song", "movie", "book", "place", "person", "experience", "project"]);

export default function MosaicCard({ item, onDelete, index = 0 }) {
  const type = MOSAIC_TYPES[item.type];
  if (!type) return null;
  const Icon = ICONS[type.icon] || Heart;
  const rot = rotationForId(item.id);
  const offsetX = offsetForId(item.id);

  return (
    <motion.article
      data-testid={`${MOSAIC.cardPrefix}${item.id}`}
      initial={{ opacity: 0, y: 16, rotate: 0 }}
      animate={{ opacity: 1, y: 0, rotate: rot }}
      transition={{ duration: 0.5, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
      whileTap={{ scale: 0.985 }}
      className="relative mx-auto"
      style={{
        marginLeft: `${offsetX}px`,
        maxWidth: "94%",
        background: type.color,
        borderRadius: "14px",
        padding: "26px 22px 22px",
        boxShadow: "0 18px 40px rgba(74,32,64,0.10), 0 1px 0 rgba(255,255,255,0.55) inset, 0 -1px 0 rgba(74,32,64,0.04) inset",
      }}
    >
      <span
        aria-hidden
        className="absolute"
        style={{
          top: "-8px",
          left: "50%",
          width: "72px",
          height: "16px",
          transform: `translateX(-50%) rotate(${rot * -1.6}deg)`,
          background: "linear-gradient(180deg, rgba(255,253,247,0.85) 0%, rgba(235,229,217,0.5) 100%)",
          boxShadow: "0 2px 4px rgba(74,32,64,0.06)",
          borderRadius: "2px",
          opacity: 0.85,
        }}
      />

      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: `${type.accent}1A` }}>
            <Icon size={12} style={{ color: type.accent }} />
          </span>
          <span className="text-[10px] uppercase tracking-[0.28em] font-medium" style={{ color: type.accent, fontFamily: "Manrope, sans-serif" }}>
            {type.label}
          </span>
        </div>
        <button
          onClick={() => onDelete(item.id)}
          aria-label="Remove piece"
          data-testid={`${MOSAIC.deletePrefix}${item.id}`}
          className="w-7 h-7 rounded-full flex items-center justify-center un-press"
          style={{ background: "rgba(74,32,64,0.05)", color: "var(--un-text-3)" }}
        >
          <Trash2 size={12} />
        </button>
      </div>

      {item.type === "quote" && (
        <div>
          <p className="text-[19px] leading-snug italic" style={{ color: "var(--un-text-1)", fontFamily: "Cormorant Garamond, serif" }}>“{item.text}”</p>
          {item.source && <p className="mt-2 text-[12px]" style={{ color: "var(--un-text-3)" }}>— {item.source}</p>}
        </div>
      )}

      {item.type === "photo" && (
        <div>
          {item.dataUrl && (
            <div className="overflow-hidden rounded-md mb-3" style={{ boxShadow: "0 4px 14px rgba(74,32,64,0.10)" }}>
              <img src={item.dataUrl} alt={item.caption || "a kept memory"} className="w-full h-auto block" loading="lazy" />
            </div>
          )}
          {item.caption && <p className="text-[15px] leading-snug italic" style={{ color: "var(--un-text-2)", fontFamily: "Cormorant Garamond, serif" }}>{item.caption}</p>}
        </div>
      )}

      {REFERENCE_TYPES.has(item.type) && (
        <div>
          <p className="text-[16px] font-medium leading-snug" style={{ color: "var(--un-text-1)" }}>{item.title || "Untitled"}</p>
          {(item.detail || item.artist) && <p className="text-[13px] mt-0.5" style={{ color: "var(--un-text-3)" }}>{item.detail || item.artist}</p>}
          {item.note && <p className="mt-2 text-[14px] italic" style={{ color: "var(--un-text-2)", fontFamily: "Cormorant Garamond, serif" }}>{item.note}</p>}
          {item.url && (
            <a href={/^https?:\/\//i.test(item.url || "") ? item.url : undefined} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full un-press" style={{ background: `${type.accent}14`, color: type.accent }}>
              Open <ExternalLink size={11} />
            </a>
          )}
        </div>
      )}

      {item.type === "message" && (
        <div>
          <p className="text-[16px] leading-relaxed italic" style={{ color: "var(--un-text-1)", fontFamily: "Cormorant Garamond, serif" }}>“{item.text}”</p>
          {item.from && <p className="mt-2 text-[12px]" style={{ color: "var(--un-text-3)" }}>from {item.from}</p>}
        </div>
      )}

      {(item.type === "reminder" || item.type === "memory") && (
        <p className="text-[16px] leading-relaxed" style={{ color: "var(--un-text-1)", fontFamily: item.type === "memory" ? "Cormorant Garamond, serif" : undefined }}>
          {item.text}
        </p>
      )}

      <p className="mt-3 text-[10px] tracking-[0.22em] uppercase" style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}>
        {formatDate(item.createdAt)}
      </p>
    </motion.article>
  );
}
